import { useMemo, type ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn, formatLakhsOrCr } from "@/lib/utils";
import { historicDashboardRanges, type DashboardRange } from "@/lib/dashboard-analytics";
import { Loader2 } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { DashboardInfoSheet } from "./DashboardInfoSheet";

interface Series { key: string; label: string; color: string }

interface DashboardLineChartProps {
  title: string;
  eyebrow: string;
  data: Array<Record<string, string | number>>;
  series: Series[];
  range: DashboardRange;
  onRangeChange: (range: DashboardRange) => void;
  valueType?: "currency" | "count";
  controls?: ReactNode;
  rangeOptions?: Array<{ value: DashboardRange; label: string }>;
  details?: ReactNode;
  detailDescription?: string;
  /** When true, shows the info (i) sheet trigger. Off by default — enable per chart. */
  showInfo?: boolean;
  loading?: boolean;
}

export function DashboardLineChart({
  title,
  data,
  series,
  range,
  onRangeChange,
  valueType = "currency",
  controls,
  rangeOptions = historicDashboardRanges,
  details,
  detailDescription,
  showInfo = false,
  loading = false,
}: DashboardLineChartProps) {
  const formatValue = (value: number) =>
    valueType === "currency" ? formatLakhsOrCr(Math.round(value)) : Math.round(value).toString();

  const seriesTotals = useMemo(() => {
    return series.map((item) => ({
      ...item,
      total: (data ?? []).reduce((sum, row) => sum + (Number(row[item.key]) || 0), 0),
    }));
  }, [data, series]);

  const grandTotal = useMemo(
    () => seriesTotals.reduce((sum, item) => sum + item.total, 0),
    [seriesTotals]
  );

  const rangeLabel =
    rangeOptions.find((item) => item.value === range)?.label ?? "Selected period";

  return (
    <Card className="dashboard-panel h-full overflow-hidden">
      <CardHeader className="space-y-3 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <CardTitle className="mt-1 text-base">{title}</CardTitle>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
              {seriesTotals.map((item) => (
                <span key={item.key} className="text-muted-foreground">
                  <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: item.color }} />
                  {item.label}:{" "}
                  <span className="font-semibold text-foreground">
                    {loading ? "—" : formatValue(item.total)}
                  </span>
                </span>
              ))}
              <span className="font-semibold text-primary">
                Total ({rangeLabel}): {loading ? "—" : formatValue(grandTotal)}
              </span>
            </div>
          </div>
          {(controls || (showInfo && details)) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {controls}
              {showInfo && details ? (
                <DashboardInfoSheet
                  title={title}
                  description={detailDescription ?? "Selected period breakdown"}
                >
                  <div className="mb-4 rounded-md border bg-muted/40 p-3 text-sm">
                    <p className="text-xs text-muted-foreground">{rangeLabel}</p>
                    <div className="mt-2 space-y-1">
                      {seriesTotals.map((item) => (
                        <div
                          key={item.key}
                          className="flex items-center justify-between gap-3"
                        >
                          <span className="text-muted-foreground">{item.label}</span>
                          <span className="font-medium tabular-nums">
                            {loading ? "—" : formatValue(item.total)}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between gap-3 border-t pt-1 font-semibold">
                        <span>Total</span>
                        <span className="tabular-nums text-primary">
                          {loading ? "—" : formatValue(grandTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {details}
                </DashboardInfoSheet>
              ) : null}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-0.5 rounded-md bg-muted p-0.5 sm:grid-cols-4" aria-label="Chart period">
          {rangeOptions.map((item) => (
            <Button
              key={item.value}
              size="sm"
              variant="ghost"
              aria-pressed={range === item.value}
              onClick={() => onRangeChange(item.value)}
              disabled={loading}
              className={cn(
                "h-7 flex-1 cursor-pointer px-2 text-[11px]",
                range === item.value && "bg-background text-foreground shadow-sm"
              )}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="relative h-[220px]">
          {loading ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              Loading chart…
            </div>
          ) : (data ?? []).length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No data found for this period.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 8, left: valueType === "currency" ? 0 : -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 4" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} allowDecimals={false} tickFormatter={formatValue} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const pointTotal = payload.reduce(
                      (sum, entry) => sum + (Number(entry.value) || 0),
                      0
                    );
                    return (
                      <div
                        className="rounded-md border px-3 py-2 text-xs shadow-sm"
                        style={{
                          backgroundColor: "hsl(var(--popover))",
                          borderColor: "hsl(var(--border))",
                        }}
                      >
                        <p className="mb-1.5 font-medium">{label}</p>
                        {payload.map((entry) => {
                          const key = String(entry.dataKey ?? entry.name ?? "");
                          const seriesItem = series.find((item) => item.key === key);
                          return (
                            <div key={key} className="flex items-center justify-between gap-4 py-0.5">
                              <span className="text-muted-foreground">{seriesItem?.label ?? key}</span>
                              <span className="font-medium tabular-nums">{formatValue(Number(entry.value) || 0)}</span>
                            </div>
                          );
                        })}
                        {series.length > 1 && (
                          <div className="mt-1 flex items-center justify-between gap-4 border-t pt-1 font-semibold">
                            <span>Total</span>
                            <span className="tabular-nums text-primary">{formatValue(pointTotal)}</span>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  formatter={(value) => series.find((item) => item.key === value)?.label ?? value}
                />
                {series.map((item) => (
                  <Line
                    key={item.key}
                    dataKey={item.key}
                    name={item.key}
                    type="linear"
                    stroke={item.color}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: item.color, stroke: item.color, strokeWidth: 1 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
